import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { pageCache } from '../utils/pageCache';
import {
  TrendingDown, Package, Flame, CheckCircle,
  Search, AlertTriangle, Calculator, X,
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { fmt, fmtK, fmtCents, fmtChangeOrNew, fmtCount, fmtPct, fmtSigned, moneyDirection } from '../utils/formatters';
import { getCategoryStyle } from '../utils/colors';
import LoadingScreen from '../components/LoadingScreen';
import DataEndsNote from '../components/DataEndsNote';
import Change from '../components/Change';

const CHART_COLORS = ['#4A2D5E', '#2E6B4F', '#7BA68E', '#B54A4A', '#7C7589', '#E6E2DE'];

function VelocityBadge({ velocity }: { velocity: string }) {
  if (velocity === 'Hot') {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-sans font-medium px-2 py-0.5 rounded-full bg-[#2E6B4F]/10 text-[#2E6B4F]">
        <Flame size={11} strokeWidth={2} /> Hot
      </span>
    );
  }
  if (velocity === 'Slow') {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-sans font-medium px-2 py-0.5 rounded-full bg-[#B54A4A]/10 text-[#B54A4A]">
        <TrendingDown size={11} strokeWidth={2} /> Slow
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-sans font-medium px-2 py-0.5 rounded-full bg-[#E6E2DE] text-[#6B6670]">
      <CheckCircle size={11} strokeWidth={2} /> Steady
    </span>
  );
}

// What-If Pricing Simulator Modal (exported for its test; the page is the only other user)
export function WhatIfModal({ product, onClose }: { product: any; onClose: () => void }) {
  const avgPrice = product.units > 0 ? product.revenue / product.units : 0;
  const [priceDeltaPct, setPriceDeltaPct] = useState(0); // -50 to +100

  const newPrice = avgPrice * (1 + priceDeltaPct / 100);
  const estimatedRevenue = product.units * newPrice;
  // The change is the difference of the two whole-dollar figures the modal
  // prints, so "$152" against "$151" reads "+$1", never "+$2" (math audit
  // 2026-10-13).
  const revenueChange = Math.round(estimatedRevenue) - Math.round(product.revenue);
  // Units held constant, so revenue moves by exactly the price change.
  const revenueChangePct = avgPrice > 0 ? priceDeltaPct : 0;
  // Tone and tint follow the whole dollars the line prints, so a change that
  // rounds to $0 reads neutral whichever way the raw sign points.
  const deltaDir = moneyDirection(revenueChange);
  const deltaTone = deltaDir === 'up' ? 'text-[#2E6B4F]' : deltaDir === 'down' ? 'text-[#B54A4A]' : 'text-[#6B6670]';
  const impactTint = deltaDir === 'up' ? 'bg-[#2E6B4F]/5 border-[#2E6B4F]/20' : deltaDir === 'down' ? 'bg-[#B54A4A]/5 border-[#B54A4A]/20' : 'bg-[#FAF9F7] border-[#E6E2DE]';

  // Price elasticity note
  const elasticityNote = priceDeltaPct > 20
    ? 'Higher prices may reduce unit volume — actual results could be lower.'
    : priceDeltaPct < -20
    ? 'Lower prices may increase volume — actual results could be higher.'
    : 'Estimate assumes constant unit volume.';

  return (
    <div className="fixed inset-0 bg-black/30 backdrop-blur-[2px] flex items-center justify-center z-50 p-4 no-print">
      <motion.div
        initial={{ opacity: 0, scale: 0.97, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        className="bg-white rounded-[18px] shadow-xl border border-[#E6E2DE] w-full max-w-md"
      >
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-[#E6E2DE]">
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <Calculator size={16} strokeWidth={1.5} className="text-[#4A2D5E]" />
              <span className="font-serif text-base text-[#2D2A2E]">What-If Simulator</span>
            </div>
            <p className="font-sans text-sm text-[#6B6670]">{product.name}</p>
          </div>
          <button onClick={onClose} className="text-[#6B6670] hover:text-[#2D2A2E] transition-colors p-1">
            <X size={18} strokeWidth={1.5} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {/* Current stats */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {[
              { label: 'Avg Price', value: fmtCents(avgPrice) },
              { label: 'Units Sold', value: fmtCount(product.units) },
              { label: 'Revenue', value: fmt(product.revenue) },
            ].map((s) => (
              <div key={s.label} className="bg-[#FAF9F7] rounded-[10px] p-3 text-center">
                <div className="text-[10px] font-sans font-medium text-[#6B6670] uppercase tracking-wide mb-1">{s.label}</div>
                <div className="text-sm font-sans font-bold text-[#2D2A2E] tabular-nums">{s.value}</div>
              </div>
            ))}
          </div>

          {/* Slider */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="font-sans text-sm text-[#2D2A2E] font-medium">Price Adjustment</span>
              <Change value={priceDeltaPct} size="sm" />
            </div>
            <input
              type="range"
              min={-50}
              max={100}
              step={5}
              value={priceDeltaPct}
              onChange={(e) => setPriceDeltaPct(Number(e.target.value))}
              className="w-full accent-[#4A2D5E]"
            />
            <div className="flex justify-between mt-1">
              <span className="text-[10px] font-sans text-[#6B6670]">−50%</span>
              <span className="text-[10px] font-sans text-[#6B6670]">0%</span>
              <span className="text-[10px] font-sans text-[#6B6670]">+100%</span>
            </div>
          </div>

          {/* New price display */}
          <div className="flex items-center justify-between bg-[#FAF9F7] rounded-[10px] px-4 py-3">
            <span className="font-sans text-sm text-[#6B6670]">New avg price</span>
            <span className="font-sans text-sm font-bold text-[#2D2A2E] tabular-nums">{fmtCents(newPrice)}</span>
          </div>

          {/* Impact card */}
          <div className={`rounded-[12px] p-4 border ${impactTint}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="font-sans text-xs font-medium text-[#6B6670] uppercase tracking-wide">Estimated Revenue</span>
              <Change value={revenueChangePct} />
            </div>
            <div className="font-sans text-2xl font-bold tabular-nums text-[#2D2A2E]">{fmt(estimatedRevenue)}</div>
            <div className={`font-sans text-sm tabular-nums mt-0.5 ${deltaTone}`}>
              {fmtSigned(revenueChange)} vs current
            </div>
          </div>

          {/* Elasticity note */}
          <p className="font-sans text-xs text-[#6B6670] text-center">{elasticityNote}</p>
        </div>

        <div className="px-6 pb-6">
          <button
            onClick={onClose}
            className="w-full bg-[#4A2D5E] text-white hover:bg-[#3A2050] rounded-[8px] h-10 font-sans font-medium text-sm transition-colors"
          >
            Done
          </button>
        </div>
      </motion.div>
    </div>
  );
}

export default function ProductsPage() {
  const [data, setData] = useState<any>(() => pageCache.get('/api/products?period=month') ?? null);
  const [loading, setLoading] = useState(() => !pageCache.has('/api/products?period=month'));
  const [period, setPeriod] = useState('month');
  const [search, setSearch] = useState('');
  const [whatIfProduct, setWhatIfProduct] = useState<any>(null);
  const [inventoryThresholds, setInventoryThresholds] = useState<Record<string, number>>({});
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    const url = `/api/products?period=${period}`;
    if (pageCache.has(url)) {
      setData(pageCache.get(url));
      setLoading(false);
    }
    axios.get(url)
      .then((res) => { pageCache.set(url, res.data); setData(res.data); })
      .catch(() => { if (!pageCache.has(url)) setData(null); })
      .finally(() => setLoading(false));
  }, [period]);

  // Fetch inventory thresholds from user settings
  useEffect(() => {
    axios.get('/api/settings').then((res) => {
      if (res.data?.inventory_thresholds) {
        setInventoryThresholds(res.data.inventory_thresholds);
      }
    }).catch(() => {});
  }, []);

  const hasData = data && data.products && data.products.length > 0;
  const filtered = data?.products?.filter((p: any) =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.category.toLowerCase().includes(search.toLowerCase())
  ) || [];

  // Products with inventory alert: threshold set and they're Hot sellers
  const hasInventoryAlerts = filtered.some(
    (p: any) => p.velocity === 'Hot' && inventoryThresholds[p.name] !== undefined
  );

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: 'easeOut' }}>
      {whatIfProduct && (
        <WhatIfModal product={whatIfProduct} onClose={() => setWhatIfProduct(null)} />
      )}

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-serif text-2xl text-[#2D2A2E]">Product Scoreboard</h1>
          <DataEndsNote dataEnds={data?.data_ends} period={period} />
        </div>
        <select value={period} onChange={(e) => setPeriod(e.target.value)} className="bg-white border border-[#E6E2DE] focus:ring-2 focus:ring-[#4A2D5E]/20 focus:border-[#4A2D5E] rounded-[8px] h-10 font-sans text-sm px-3 outline-none w-44">
          <option value="week">This Week</option>
          <option value="month">This Month</option>
          <option value="quarter">Last 90 Days</option>
        </select>
      </div>

      {loading ? (
        <LoadingScreen fullScreen={false} />
      ) : !hasData ? (
        <div className="flex flex-col items-center justify-center py-24">
          <Package size={48} strokeWidth={1.5} className="text-[#4A2D5E]/30 mb-4" />
          <h3 className="font-serif text-xl text-[#2D2A2E] mb-2">No product data yet</h3>
          <p className="font-sans text-sm text-[#6B6670] mb-6 text-center max-w-sm">Upload your transaction data to see how each product is performing.</p>
          <button onClick={() => navigate('/settings/data')} className="bg-[#2E6B4F] text-white hover:bg-[#245A41] rounded-[8px] shadow-sm font-sans font-medium px-6 py-2.5 transition-colors">Upload Data</button>
        </div>
      ) : (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-white border border-[#E6E2DE] rounded-[14px] p-6 shadow-card">
              <div className="text-xs font-sans font-medium text-[#6B6670] uppercase tracking-wide mb-1">Total Products</div>
              <div className="text-2xl font-sans font-bold text-[#2D2A2E] tabular-nums">{fmtCount(data.kpis.total_products.value)}</div>
            </div>

            <div className="bg-white border border-[#E6E2DE] rounded-[14px] p-6 shadow-card">
              <div className="text-xs font-sans font-medium text-[#2E6B4F] uppercase tracking-wide mb-1">Top Performer</div>
              {data.top_performer ? (
                <>
                  <div className="text-lg font-sans font-bold text-[#2D2A2E] truncate">{data.top_performer.name}</div>
                  <div className="text-sm font-sans text-[#2E6B4F] tabular-nums">{fmt(data.top_performer.revenue)}</div>
                </>
              ) : (
                <div className="text-lg font-sans font-bold text-[#6B6670]">—</div>
              )}
            </div>

            <div className="bg-white border border-[#E6E2DE] rounded-[14px] p-6 shadow-card">
              <div className="text-xs font-sans font-medium text-[#4A2D5E] uppercase tracking-wide mb-1">Rising Star</div>
              {data.rising_star ? (
                <>
                  <div className="text-lg font-sans font-bold text-[#2D2A2E] truncate">{data.rising_star.name}</div>
                  <div>
                    {data.rising_star.change != null && <Change value={data.rising_star.change} size="sm" />}
                  </div>
                </>
              ) : (
                <div className="text-lg font-sans font-bold text-[#6B6670]">—</div>
              )}
            </div>

            <div className="bg-white border border-[#E6E2DE] rounded-[14px] p-6 shadow-card">
              <div className={`text-xs font-sans font-medium uppercase tracking-wide mb-1 ${data.needs_review > 0 ? 'text-[#B54A4A]' : 'text-[#6B6670]'}`}>Needs Review</div>
              <div className={`text-2xl font-sans font-bold tabular-nums ${data.needs_review > 0 ? 'text-[#B54A4A]' : 'text-[#2D2A2E]'}`}>
                {fmtCount(data.needs_review)}
              </div>
              {data.needs_review > 0 && (
                <div className="flex items-center gap-1 mt-1">
                  <AlertTriangle size={12} strokeWidth={1.5} className="text-[#B54A4A]" />
                  <span className="text-xs font-sans text-[#B54A4A]">Products declining</span>
                </div>
              )}
            </div>
          </div>

          {/* Inventory Alert Banner */}
          {hasInventoryAlerts && (
            <div className="flex items-start gap-3 p-4 bg-[#B54A4A]/5 border border-[#B54A4A]/20 rounded-[12px] mb-6 no-print">
              <AlertTriangle size={18} strokeWidth={1.5} className="text-[#B54A4A] shrink-0 mt-0.5" />
              <div>
                <p className="font-sans text-sm font-medium text-[#B54A4A]">Inventory Alert</p>
                <p className="font-sans text-xs text-[#6B6670] mt-0.5">
                  Some hot-selling products are near your configured stock thresholds. Check the table below for details.
                </p>
              </div>
            </div>
          )}

          {/* Category chart */}
          <div className="bg-white border border-[#E6E2DE] rounded-[14px] p-6 shadow-card mb-8">
            <h3 className="font-serif text-lg text-[#2D2A2E] mb-4">Revenue by Category</h3>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={data.categories} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#E6E2DE" />
                <XAxis type="number" tick={{ fill: '#6B6670', fontSize: 12, fontFamily: 'DM Sans' }} tickFormatter={(v) => fmtK(v)} />
                <YAxis type="category" dataKey="name" tick={{ fill: '#6B6670', fontSize: 12, fontFamily: 'DM Sans' }} width={100} />
                <Tooltip contentStyle={{ borderRadius: '10px', border: '1px solid #E6E2DE', fontFamily: 'DM Sans', fontSize: 12 }} formatter={(value: number) => [fmt(value), 'Revenue']} />
                <Bar dataKey="revenue" radius={[0, 6, 6, 0]}>
                  {data.categories.map((_: any, i: number) => (
                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Search bar */}
          <div className="relative mb-4">
            <Search size={16} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6670]" />
            <input
              type="text"
              placeholder="Search products..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-white border border-[#E6E2DE] rounded-[8px] h-10 pl-9 pr-3 font-sans text-sm text-[#2D2A2E] placeholder:text-[#6B6670]/50 outline-none focus:ring-2 focus:ring-[#4A2D5E]/20 focus:border-[#4A2D5E]"
            />
          </div>

          {/* Product table */}
          <div className="bg-white border border-[#E6E2DE] rounded-[14px] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#FAF9F7] border-b border-[#E6E2DE]">
                    {['#', 'Product', 'Revenue', 'Units', 'Avg Price', 'Margin', 'Trend', 'Velocity', 'Simulate'].map((h) => (
                      <th
                        key={h}
                        className={`p-4 text-xs font-medium text-[#6B6670] uppercase tracking-wide font-sans ${
                          h === '#' || h === 'Product' ? 'text-left' : 'text-right'
                        } ${h === 'Velocity' || h === 'Simulate' ? 'text-center' : ''} ${h === 'Simulate' ? 'no-print' : ''}`}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p: any, i: number) => {
                    const catStyle = getCategoryStyle(p.category);
                    const avgPrice = p.units > 0 ? p.revenue / p.units : 0;
                    const hasThreshold = inventoryThresholds[p.name] !== undefined;
                    const showInventoryAlert = p.velocity === 'Hot' && hasThreshold;
                    return (
                      <tr key={p.name} className="border-b border-[#E6E2DE] last:border-0 hover:bg-[#FAF9F7] transition-colors">
                        <td className="p-4 text-sm font-sans text-[#6B6670] tabular-nums">{i + 1}</td>
                        <td className="p-4">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-sans text-[#2D2A2E] font-medium">{p.name}</span>
                            <span className={`text-[10px] font-sans font-medium px-1.5 py-0.5 rounded-full ${catStyle.bg} ${catStyle.text}`}>
                              {p.category}
                            </span>
                            {showInventoryAlert && (
                              <span className="inline-flex items-center gap-1 text-[10px] font-sans font-medium px-1.5 py-0.5 rounded-full bg-[#B54A4A]/10 text-[#B54A4A]">
                                <AlertTriangle size={9} strokeWidth={2} /> Monitor Stock
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="p-4 text-sm font-sans text-[#2D2A2E] text-right tabular-nums">{fmt(p.revenue)}</td>
                        <td className="p-4 text-sm font-sans text-[#2D2A2E] text-right tabular-nums">{fmtCount(p.units)}</td>
                        <td className="p-4 text-sm font-sans text-[#2D2A2E] text-right tabular-nums">{fmtCents(avgPrice)}</td>
                        <td className="p-4 text-sm font-sans text-[#2D2A2E] text-right tabular-nums">{fmtPct(p.margin)}</td>
                        <td className="p-4 text-right">
                          {p.revenue_change == null
                            ? <span className="font-sans text-sm text-[#6B6670]">{fmtChangeOrNew(p.revenue_change, p.revenue)}</span>
                            : <Change value={p.revenue_change} size="sm" />}
                        </td>
                        <td className="p-4 text-center">
                          <VelocityBadge velocity={p.velocity} />
                        </td>
                        <td className="p-4 text-center no-print">
                          <button
                            onClick={() => setWhatIfProduct(p)}
                            title="Open What-If Pricing Simulator"
                            className="inline-flex items-center gap-1 text-[11px] font-sans font-medium px-2 py-1 rounded-[6px] bg-[#4A2D5E]/8 text-[#4A2D5E] hover:bg-[#4A2D5E]/15 transition-colors"
                          >
                            <Calculator size={11} strokeWidth={1.5} /> Simulate
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* What-If info note */}
          <p className="font-sans text-xs text-[#6B6670] mt-3 no-print">
            Click <span className="font-medium">Simulate</span> on any product to model the revenue impact of a price change.
          </p>
        </>
      )}
    </motion.div>
  );
}
